import * as errors              from '../util/errors';
import { getFileURL }           from '../util/file_entry';
import { hideFormAccessoryBar } from './field_without_accessory_bar';
import autosize                 from 'autosize';
import ButtonWithoutPropagation from './button_without_propagation';
import classnames               from 'classnames';
import F7Event                  from '../app/events/f7_event';
import FieldWithoutAccessoryBar from './field_without_accessory_bar';
import PropTypes                from 'prop-types';
import React                    from 'react';
import store                    from '../store';


export default class MessageBar extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      message:      '',
      warning:      '',
      selectedFile: {},
      changed:      false,
      height:       0,
      visible:      true
    };

    this.messagebar = React.createRef();
    this.textarea   = React.createRef();

    this.canSendMessage     = this.canSendMessage.bind(this);
    this.focusInTextarea    = this.focusInTextarea.bind(this);
    this.getAttachments     = this.getAttachments.bind(this);
    this.hasAttachment      = this.hasAttachment.bind(this);
    this.initTextarea       = this.initTextarea.bind(this);
    this.onChange           = this.onChange.bind(this);
    this.onFileRemove       = this.onFileRemove.bind(this);
    this.onSelectedFile     = this.onSelectedFile.bind(this);
    this.onSend             = this.onSend.bind(this);
    this.updateHeight       = this.updateHeight.bind(this);
    this.getBytesFromString = this.getBytesFromString.bind(this);
    this.isValidMaxBytes    = this.isValidMaxBytes.bind(this);
    this.showMessageBar     = this.showMessageBar.bind(this);
    this.hideMessageBar     = this.hideMessageBar.bind(this);
  }

  componentDidMount() {
    this.updateHeight();
    if (this.props.enabled)
      this.initTextarea();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.enabled === false && this.props.enabled === true)
      this.initTextarea();
  }

  initTextarea() {
    autosize(this.textarea.current);
  }

  onChange({ target }) {
    const { maxBytes } = this.props;
    const message      = target.value;
    const messageBytes = this.getBytesFromString(message);

    this.setState({ message, changed: true });
    this.updateHeight();

    if (maxBytes && (messageBytes > maxBytes))
      this.setState({ warning: `Please do not exceed ${maxBytes} chars` });
    else
      this.setState({ warning: '' });
  }

  focusInTextarea() {
    hideFormAccessoryBar(true, () => {
      if (this.textarea.current)
        this.textarea.current.focus();
    });
  }

  hasAttachment() {
    return !!this.state.selectedFile.name;
  }

  getAttachments() {
    if (this.hasAttachment())
      return [ this.state.selectedFile ];
    else
      return null;
  }

  canSendMessage() {
    const { message }     = this.state;
    const attachments     = this.getAttachments();
    const notEmptyMessage = message.trim().length > 0;
    const isValidMaxBytes = this.isValidMaxBytes();
    return attachments || (notEmptyMessage && isValidMaxBytes);
  }

  onSend() {
    const { message } = this.state;
    const { onSend }  = this.props;
    const attachments = this.getAttachments();

    if (this.canSendMessage()) {
      onSend({ attachments, body: message.trim() });
      this.setState({ changed: false, message: '', selectedFile: {} }, () => {
        autosize.update(this.textarea.current);
        this.updateHeight();
      });
    }

    this.focusInTextarea();
  }

  onSelectedFile(selectedFile) {
    this.setState({ selectedFile });
  }

  updateHeight() {
    const height = this.messagebar.current.clientHeight;
    if (height !== this.state.height) {
      this.setState({ height }, () => {
        if (this.props.onHeightChange)
          this.props.onHeightChange(height);
      });
    }
  }

  onFileRemove() {
    this.setState({ selectedFile: {} }, () => {
      this.updateHeight();
    });
  }

  hideMessageBar() {
    this.setState({ visible: false });
  }

  showMessageBar() {
    this.setState({ visible: true });
  }

  isValidMaxBytes() {
    const { message }  = this.state;
    const { maxBytes } = this.props;

    if (maxBytes) {
      const messageBytes = this.getBytesFromString(message);
      return messageBytes <= maxBytes;
    } else
      return true;
  }

  getBytesFromString(str) {
    // https://stackoverflow.com/a/23329386/2734665
    // returns the byte length of an utf8 string
    let s = str.length;
    for (let i = str.length - 1; i >= 0; i--) {
      const code = str.charCodeAt(i);
      if (code > 0x7f && code <= 0x7ff)
        s++;
      else if (code > 0x7ff && code <= 0xffff)
        s += 2;
      if (code >= 0xDC00 && code <= 0xDFFF)
        i--; // trail surrogate
    }
    return s;
  }

  render() {
    const { children }            = this.props;
    const { disabledPlaceholder } = this.props;
    const { enabled }             = this.props;
    const { leftButtons }         = this.props;
    const { maxLength }           = this.props;
    const { message }             = this.state;
    const { placeholder }         = this.props;
    const { rightButtons }        = this.props;
    const { selectedFile }        = this.state;
    const { showToastError }      = this.props;
    const { onKeyDown }           = this.props;
    const { warning }             = this.state;

    const { focusInTextarea } = this;
    const { showMessageBar }  = this;
    const { hideMessageBar }  = this;
    const isVisible           = this.state.visible;

    const render           = children && children({ focusInTextarea, showMessageBar, hideMessageBar });
    const selectedFileURL  = selectedFile && getFileURL(selectedFile.nativeURL);
    const containerClasses = classnames('messagebar-container', { 'with-attachment': this.hasAttachment() });

    return (
      <div className={containerClasses}>
        <div className="messagebar-above">
          {render}
        </div>
        {isVisible && (
          <div className="toolbar messagebar" ref={this.messagebar}>
            {enabled && (
              <React.Fragment>
                <div className="toolbar-inner">
                  <div className="messagebar-area">
                    <FieldWithoutAccessoryBar>
                      {({ onTouchStart, onBlur }) => (
                        <div>
                          {this.hasAttachment() &&
                          <FileThumbnail src={selectedFileURL} onLoad={this.updateHeight} onRemove={this.onFileRemove}/>
                          }
                          <textarea
                            id="messagebar-textarea"
                            placeholder={placeholder}
                            value={message}
                            onTouchStart={onTouchStart}
                            onBlur={onBlur}
                            disabled={!enabled}
                            maxLength={maxLength}
                            onChange={this.onChange}
                            onKeyDown={onKeyDown}
                            ref={this.textarea}
                          />
                        </div>
                      )}
                    </FieldWithoutAccessoryBar>
                  </div>
                </div>
                <div className="messagebar__buttons">
                  <div className="messagebar__button">
                    {window.navigator.camera && <AttachPhotoButton onError={showToastError} onSelectedFile={this.onSelectedFile}/>}
                    <AttachFileButton onSelectedFile={this.onSelectedFile}/>
                    {leftButtons}
                    {warning && <span className="warning">{warning}</span>}
                  </div>
                  <div className="messagebar__button messagebar__button-right">
                    {rightButtons}
                    <ButtonWithoutPropagation onSubmit={this.onSend} disabled={!this.canSendMessage()}/>
                  </div>
                </div>
              </React.Fragment>
            )}

            {!enabled && disabledPlaceholder}

          </div>
        )}
      </div>
    );
  }
}

MessageBar.propTypes = {
  children:            PropTypes.func,
  disabledPlaceholder: PropTypes.element,
  enabled:             PropTypes.bool,
  leftButtons:         PropTypes.element,
  onSend:              PropTypes.func.isRequired,
  placeholder:         PropTypes.string,
  rightButtons:        PropTypes.element,
  showToastError:      PropTypes.func,
  maxLength:           PropTypes.number,
  maxBytes:            PropTypes.number,
  onKeyDown:           PropTypes.func
};

MessageBar.defaultProps = {
  children:       () => {},
  enabled:        true,
  showToastError: error => store.dispatch(errors.showToastError(error)),
  placeholder:    '',
  onKeyDown:      () => {}
};


class FileThumbnail extends React.Component {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.props.onLoad();
  }

  render() {
    const { onLoad }   = this.props;
    const { onRemove } = this.props;
    const { src }      = this.props;

    return (
      <div className="attached-file-wrapper">
        <i className="f7-icons close-button" onClick={onRemove}>close_round</i>
        {src && <img className="attached-file" onLoad={onLoad} src={src}/>}
        {!src && <i className="f7-icons attachment-icon-preview">document</i>}
      </div>
    );
  }
}

function AttachPhotoButton({ onSelectedFile, onError }) {
  const { camera } = window.navigator;

  async function getImage(options) {
    try {
      const uri = await new Promise(function(resolve, reject) {
        camera.getPicture(resolve, reject, options);
      });

      const fileEntry = await new Promise(function(resolve, reject) {
        window.resolveLocalFileSystemURL(uri, resolve, reject);
      });

      onSelectedFile(fileEntry);
    } catch (error) {
      const androidCancelled = (error === 'No Image Selected');
      const iOSCancelled     = (error === 'has no access to assets');
      const cancelled        = (iOSCancelled || androidCancelled);
      if (!cancelled)
        onError('An error occurred. Please check that the app has permission to access the photo gallery.');
    }
  }

  function openActionSheet({ target }) {
    target.dispatchEvent(new F7Event(app => {
      const actionSheet = app.actions.create({
        buttons: [
          {
            text: 'Choose from Library',
            onClick() {
              const sourceType = camera.PictureSourceType.PHOTOLIBRARY;
              getImage({ sourceType });
            }
          },
          {
            text: 'Take Photo',
            onClick() {
              const sourceType = camera.PictureSourceType.CAMERA;
              getImage({ sourceType });
            }
          },
          {
            text:  'Cancel',
            color: 'red'
          }
        ]
      });

      actionSheet.open();
    }));
  }

  return (
    <a className="link icon-only attach-photo-button" onClick={openActionSheet}>
      <span><i className="icon f7-icons">camera_alt</i></span>
    </a>
  );
}

function AttachFileButton({ onSelectedFile }) {
  // dynamic id, to avoid htmlFor to point to previous page
  const targetId = `attach-file-button-input-${Date.now()}${Math.random()}`;
  function onFileChange({ target }) {
    const file = target.files[0];
    onSelectedFile(file);
    target.value = null; // eslint-disable-line no-param-reassign
  }

  return (
    <div className="link icon-only attach-file-button">
      <label htmlFor={targetId}>
        <i className="icon icons8 icons8-attatchment"/>
      </label>
      <input type="file" id={targetId} className="attach-file-button-input" onChange={onFileChange}/>
    </div>
  );
}
